import { createSearchParams } from "react-router-dom";
import { EditButton } from "../../shared/layout/EditButton";
import { DeleteButton } from "../../shared/layout/DeleteButton";
import {
  Button,
  Dropdown,
  Flex,
  Tag,
  Tooltip,
  type ButtonProps,
  type MenuProps,
} from "antd";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../shared/utils/translationUtils";
import {
  ProtocolCreationAmplitudeEvent,
  ProtocolTemplatesAmplitudeEvent,
} from "../../shared/utils/amplitudeEvents";
import { formatDate } from "../create-protocol/utils";
import { ArchiveButton } from "../../shared/layout/ArchiveButton";
import { UnarchiveButton } from "../../shared/layout/UnarchiveButton";
import useTemplateIcon from "../../assets/images/use-template.svg";
import {
  isOrgAdminOperationAllowed,
  isOrgCollaboratorOnlyOperationAllowed,
  isWorkspaceOwnerOperationAllowed,
} from "./permissions";
import { type PermissionOperation } from "../../shared/utils/BaseClient";
import { ReactComponent as MenuIcon } from "../../assets/images/Menu.svg";
import { useProtocolsRouter } from "../../shared/custom-hooks/useProtocolsRouter";
import { useSyncPermissions } from "../../shared/custom-hooks/useSyncPermissions";
import { useTrackingEvent } from "../../shared/custom-hooks/useTracking";
import { useFeatureFlags } from "../../context/LDFlagsContext";

interface ExecutionStatusCounts {
  activated?: number;
  ready?: number;
  draft?: number;
}

interface ProtocolRecord {
  name: string;
  id: string;
  crop?: string;
  crop_type?: string;
  trial_count: string;
  status: string;
  complete?: boolean;
  trial_execution_summary?: ExecutionStatusCounts;
}

type TranslateText = (key: string) => string;
type CheckTrailCount = (val: string) => number;
type TrackProtocolEvents = ReturnType<typeof useTrackingEvent>;

/**
 * Maps a protocol type to the tag class and translated label shown in the
 * protocols table.
 *
 * @param type Raw protocol type value from the table data.
 * @param getTranslatedText Translation helper for localized labels.
 * @returns The tag class name and display label for the protocol type.
 */
const getProtocolTypeDetails = (
  type: string,
  getTranslatedText: (key: string) => string
): { colorType: string; typeName: string } => {
  if (type === "seed") {
    return {
      colorType: "seedType",
      typeName: getTranslatedText("trialtypes.label.seed"),
    };
  }

  if (type === "nutrient") {
    return {
      colorType: "nutrientType",
      typeName: getTranslatedText("trialtypes.label.fertiliser"),
    };
  }

  return {
    colorType: "cropProtectionType",
    typeName: getTranslatedText("trialtypes.label.cropProtection"),
  };
};

/**
 * Renders translated protocol type tags for the table cell.
 *
 * @param types Protocol type value or values associated with the row.
 * @param getTranslatedText Translation helper for localized labels.
 * @returns A tag list for the protocol type cell.
 */
const renderProtocolTypeTags = (
  types: string | string[] | null | undefined,
  getTranslatedText: (key: string) => string
): JSX.Element => {
  const protocolTypes = Array.isArray(types) ? types : types ? [types] : [];

  return (
    <span>
      {protocolTypes.map((type: string) => {
        const { colorType, typeName } = getProtocolTypeDetails(
          type,
          getTranslatedText
        );

        return (
          <Tag className={colorType} key={type}>
            {typeName}
          </Tag>
        );
      })}
    </span>
  );
};

/**
 * Renders the activated/ready/draft trial breakdown for the protocol's
 * execution status cell, sourced from the row's `trial_execution_summary`.
 *
 * @param executionSummary Per-status trial counts from the row.
 * @param getTranslatedText Translation helper for localized labels.
 * @returns The execution status cell content.
 */
const renderExecutionStatus = (
  executionSummary: ExecutionStatusCounts | undefined,
  getTranslatedText: (key: string) => string
): JSX.Element => {
  const { activated = 0, ready = 0, draft = 0 } = executionSummary ?? {};

  return (
    <span className="execution-status-cell">
      {activated > 0 && (
        <span className="execution-status-activated">
          {activated} {getTranslatedText("protocols.executionStatusActivated")}
        </span>
      )}
      {ready > 0 && (
        <span className="execution-status-ready">
          {ready} {getTranslatedText("protocols.executionStatusReady")}
        </span>
      )}
      {draft > 0 && (
        <span className="execution-status-draft">
          {draft} {getTranslatedText("protocols.executionStatusDraft")}
        </span>
      )}
    </span>
  );
};

/**
 * Checks whether a protocol already has trials assigned.
 *
 * @param trialCount Trial count value from the row.
 * @param checkTrailCount Parser used to normalize the count.
 * @returns `true` when the protocol has one or more linked trials.
 */
const hasTrialsAssigned = (
  trialCount: string,
  checkTrailCount: CheckTrailCount
): boolean => {
  return checkTrailCount(trialCount) > 0;
};

interface ProtocolUnarchiveActionButtonProps {
  recordId: string;
  getTranslatedText: TranslateText;
  handleUnarchive: (id: string) => Promise<void>;
}

/**
 * Renders the unarchive action button for archived protocols.
 *
 * @param props Button dependencies and record identifier.
 * @returns The unarchive action button.
 */
const ProtocolUnarchiveActionButton = ({
  recordId,
  getTranslatedText,
  handleUnarchive,
}: ProtocolUnarchiveActionButtonProps): JSX.Element => {
  return (
    <UnarchiveButton
      onClick={async () => {
        await handleUnarchive(recordId);
      }}
    >
      {getTranslatedText("createProtocol.table.unarchive")}
    </UnarchiveButton>
  );
};

interface ProtocolEditActionButtonProps {
  checkTrailCount: CheckTrailCount;
  getTranslatedText: TranslateText;
  trackProtocolEvents: TrackProtocolEvents;
  record: ProtocolRecord;
}

/**
 * Renders the edit action button and routes to the protocol edit flow.
 *
 * @param props Button dependencies and row data.
 * @returns The edit action button.
 */
const ProtocolEditActionButton = ({
  checkTrailCount,
  getTranslatedText,
  trackProtocolEvents,
  record,
}: ProtocolEditActionButtonProps): JSX.Element => {
  const isDisabled = hasTrialsAssigned(record.trial_count, checkTrailCount);
  const { router: protocolsRouter } = useProtocolsRouter();

  return (
    <EditButton
      from="protocols"
      onClick={() => {
        const event = window.event;
        event?.stopPropagation();
        void trackProtocolEvents({
          event: ProtocolCreationAmplitudeEvent.PROTOCOL_ACTION_CLICKED,
          pro_action_type: "Edit",
          protocol_id: record.id,
          crop_type: record.crop_type ?? record.crop,
        });
        protocolsRouter.goToProtocolsEditPage(
          createSearchParams({ protocol_id: record.id }).toString(),
          record.id,
          "trial-type"
        );
      }}
      disabled={isDisabled}
    >
      <Flex align="center" gap={8}>
        {getTranslatedText("createProtocol.table.edit")}
      </Flex>
    </EditButton>
  );
};

interface ProtocolArchiveActionButtonProps {
  getTranslatedText: TranslateText;
  handleArchive: (id: string) => Promise<void>;
  record: ProtocolRecord;
  trackProtocolEvents: TrackProtocolEvents;
}

/**
 * Renders the archive action button for active protocols.
 *
 * @param props Button dependencies and row data.
 * @returns The archive action button.
 */
const ProtocolArchiveActionButton = ({
  getTranslatedText,
  handleArchive,
  record,
  trackProtocolEvents,
}: ProtocolArchiveActionButtonProps): JSX.Element => {
  return (
    <ArchiveButton
      from="protocols"
      onClick={async () => {
        void trackProtocolEvents({
          event: ProtocolCreationAmplitudeEvent.PROTOCOL_ACTION_CLICKED,
          pro_action_type: "Archive",
          protocol_id: record.id,
          crop_type: record.crop_type ?? record.crop,
        });
        await handleArchive(record.id);
      }}
      status={record.status}
    >
      <Flex align="center" gap={8}>
        {getTranslatedText("createProtocol.table.archive")}
      </Flex>
    </ArchiveButton>
  );
};

interface DeleteActionButtonProps {
  checkTrailCount: CheckTrailCount;
  getTranslatedText: TranslateText;
  handleDelete: (id: string) => void;
  trackProtocolEvents: TrackProtocolEvents;
  record: ProtocolRecord;
  activeTab: string;
}

/**
 * Renders the delete action button and applies the disabled state when trials
 * are already linked to the protocol.
 *
 * @param props Button dependencies and row data.
 * @returns The delete action button.
 */
const DeleteActionButton = ({
  checkTrailCount,
  getTranslatedText,
  handleDelete,
  record,
  activeTab,
  trackProtocolEvents,
}: DeleteActionButtonProps): JSX.Element => {
  const isDisabled = hasTrialsAssigned(record.trial_count, checkTrailCount);

  return (
    <DeleteButton
      onClick={() => {
        void trackProtocolEvents({
          event: ProtocolCreationAmplitudeEvent.PROTOCOL_ACTION_CLICKED,
          pro_action_type: "Delete",
          protocol_id: record.id,
          crop_type: record.crop_type ?? record.crop,
        });
        if (activeTab === "templates") {
          void trackProtocolEvents({
            event: ProtocolTemplatesAmplitudeEvent.DELETE_TEMPLATE,
            originAction: "delete-template",
          });
        }
        handleDelete(record.id);
      }}
      disabled={isDisabled}
      dataName={record.name}
      from={activeTab === "templates" ? "template" : "protocols"}
    >
      <Flex
        align="center"
        gap={8}
        style={{
          opacity: isDisabled ? 0.8 : 1,
          cursor: isDisabled ? "not-allowed" : "pointer",
        }}
      >
        {getTranslatedText("createProtocol.table.delete")}
      </Flex>
    </DeleteButton>
  );
};

/**
 * Renders the dropdown trigger button for protocol row actions.
 *
 * @param props Props forwarded from Ant Design's dropdown trigger.
 * @returns The action menu trigger button.
 */
const ProtocolActionsMenuButton = (props: ButtonProps): JSX.Element => {
  const { onClick, ...buttonProps } = props;

  return (
    <Button
      {...buttonProps}
      className="action-btn"
      type="text"
      icon={<MenuIcon className="menu-icon" />}
      onClick={(e) => {
        onClick?.(e);
        e.stopPropagation();
        e.preventDefault();
      }}
      style={{
        padding: 0,
        border: "none",
        boxShadow: "none",
        width: 32,
        height: 32,
      }}
    />
  );
};

interface ProtocolActionMenuItemsProps {
  checkTrailCount: CheckTrailCount;
  getTranslatedText: TranslateText;
  handleArchive: (id: string) => Promise<void>;
  handleDelete: (id: string) => void;
  trackProtocolEvents: TrackProtocolEvents;
  record: ProtocolRecord;
  activeTab: string;
  handleOpenTemplateModal: (protocolId: string, protocolName: string) => void;
  enableProtocolTemplates: boolean;
}

interface UseTemplateActionButtonProps {
  getTranslatedText: TranslateText;
  record: ProtocolRecord;
  trackProtocolEvents: TrackProtocolEvents;
}

/**
 * Renders the edit action button and routes to the protocol edit flow.
 *
 * @param props Button dependencies and row data.
 * @returns The edit action button.
 */
const UseTemplateActionButton = ({
  getTranslatedText,
  record,
  trackProtocolEvents,
}: UseTemplateActionButtonProps): JSX.Element => {
  const { router: protocolsRouter } = useProtocolsRouter();

  return (
    <Tooltip
      data-testid="tooltip-text"
      placement="left"
      title={getTranslatedText("templates.useTemplateButtonTooltipText")}
    >
      <Button
        className="use-template-action-btn"
        icon={
          <img
            src={useTemplateIcon}
            alt={getTranslatedText("createProtocol.table.useTemplate")}
            width={18}
          />
        }
        onClick={(e) => {
          e.stopPropagation();
          void trackProtocolEvents({
            event: ProtocolCreationAmplitudeEvent.PROTOCOL_ACTION_CLICKED,
            pro_action_type: "Template",
            protocol_id: record.id,
            crop_type: record.crop_type ?? record.crop,
          });
          void trackProtocolEvents({
            event: ProtocolCreationAmplitudeEvent.PROTOCOL_CREATION_STARTED,
            creation_method: "Template",
            template_id: record.id,
            crop_type: record.crop_type ?? record.crop,
          });
          void trackProtocolEvents({
            event: ProtocolTemplatesAmplitudeEvent.USE_TEMPLATE,
            originAction: "use-template",
          });
          protocolsRouter.goToProtocolsCreatePage(
            createSearchParams({
              template_id: record.id,
              template_name: record.name,
            }).toString(),
            "trial-type"
          );
        }}
        style={{
          border: "none",
          boxShadow: "none",
          padding: 0,
        }}
      >
        {getTranslatedText("createProtocol.table.useTemplate")}
      </Button>
    </Tooltip>
  );
};

interface SaveAsTemplateActionButtonProps {
  getTranslatedText: TranslateText;
  record: ProtocolRecord;
  handleOpenTemplateModal: (protocolId: string, protocolName: string) => void;
  trackProtocolEvents: TrackProtocolEvents;
}

const SaveAsTemplateActionButton = ({
  getTranslatedText,
  record,
  handleOpenTemplateModal,
  trackProtocolEvents,
}: SaveAsTemplateActionButtonProps): JSX.Element => {
  return (
    <Tooltip
      data-testid="tooltip-text"
      placement="left"
      title={getTranslatedText("protocols.saveAsTemplateButtonTooltipText")}
    >
      <Button
        className="use-template-action-btn"
        icon={
          <img
            src={useTemplateIcon}
            alt={getTranslatedText("protocols.saveAsTemplateButton")}
            height={18}
          />
        }
        onClick={(e) => {
          e.stopPropagation();
          void trackProtocolEvents({
            event: ProtocolCreationAmplitudeEvent.PROTOCOL_ACTION_CLICKED,
            pro_action_type: "Template",
            protocol_id: record.id,
            crop_type: record.crop_type ?? record.crop,
          });
          void trackProtocolEvents({
            event:
              ProtocolTemplatesAmplitudeEvent.SAVE_AS_TEMPLATE_WORKSPACE_UNIVERSAL,
            originAction: "save-as-template-workspace-universal",
          });
          handleOpenTemplateModal(record.id, record.name);
        }}
        style={{
          border: "none",
          boxShadow: "none",
          padding: 0,
        }}
      >
        {getTranslatedText("protocols.saveAsTemplateButton")}
      </Button>
    </Tooltip>
  );
};

/**
 * Builds the dropdown menu items for the actions column.
 *
 * @param props Action handlers, translation helpers, and row data.
 * @returns Ant Design menu items for the row action menu.
 */
const getProtocolActionMenuItems = ({
  checkTrailCount,
  getTranslatedText,
  handleArchive,
  handleDelete,
  trackProtocolEvents,
  record,
  activeTab,
  handleOpenTemplateModal,
  enableProtocolTemplates,
}: ProtocolActionMenuItemsProps): MenuProps["items"] => {
  const isDeleteDisabled = hasTrialsAssigned(
    record.trial_count,
    checkTrailCount
  );

  const activeProtocolActions = [
    {
      key: "edit",
      label: (
        <div className="dropdown-item">
          <ProtocolEditActionButton
            checkTrailCount={checkTrailCount}
            getTranslatedText={getTranslatedText}
            trackProtocolEvents={trackProtocolEvents}
            record={record}
          />
        </div>
      ),
      style: {
        padding: 0,
        backgroundColor: "transparent",
      },
    },
    {
      key: "archive",
      label: (
        <div className="dropdown-item">
          <ProtocolArchiveActionButton
            getTranslatedText={getTranslatedText}
            handleArchive={handleArchive}
            record={record}
            trackProtocolEvents={trackProtocolEvents}
          />
        </div>
      ),
      onClick: (e: Parameters<NonNullable<MenuProps["onClick"]>>[0]) => {
        e.domEvent.stopPropagation();
      },
      style: {
        padding: 0,
        backgroundColor: "transparent",
      },
    },
  ];

  if (
    (record.status === "published" || record.complete === true) &&
    enableProtocolTemplates
  ) {
    activeProtocolActions.push({
      key: "saveAstemplate",
      label: (
        <div className="dropdown-item">
          <SaveAsTemplateActionButton
            getTranslatedText={getTranslatedText}
            record={record}
            handleOpenTemplateModal={handleOpenTemplateModal}
            trackProtocolEvents={trackProtocolEvents}
          />
        </div>
      ),
      onClick: (e: Parameters<NonNullable<MenuProps["onClick"]>>[0]) => {
        e.domEvent.stopPropagation();
      },
      style: {
        padding: 0,
        backgroundColor: "transparent",
      },
    });
  }

  const templateActions = [
    {
      key: "use_template",
      label: (
        <div className="dropdown-item">
          <UseTemplateActionButton
            getTranslatedText={getTranslatedText}
            record={record}
            trackProtocolEvents={trackProtocolEvents}
          />
        </div>
      ),
      onClick: (e: Parameters<NonNullable<MenuProps["onClick"]>>[0]) => {
        e.domEvent.stopPropagation();
      },
      style: {
        padding: 0,
        backgroundColor: "transparent",
      },
    },
  ];

  return [
    ...(activeTab === "active" ? activeProtocolActions : templateActions),
    {
      key: "delete",
      label: (
        <div className="dropdown-item">
          <DeleteActionButton
            checkTrailCount={checkTrailCount}
            getTranslatedText={getTranslatedText}
            handleDelete={handleDelete}
            record={record}
            activeTab={activeTab}
            trackProtocolEvents={trackProtocolEvents}
          />
        </div>
      ),
      style: {
        padding: 0,
        backgroundColor: "transparent",
      },
      disabled: isDeleteDisabled,
    },
  ];
};

interface RenderProtocolActionsProps {
  activeTab: string;
  checkTrailCount: CheckTrailCount;
  getTranslatedText: TranslateText;
  handleArchive: (id: string) => Promise<void>;
  handleDelete: (id: string) => void;
  handleUnarchive: (id: string) => Promise<void>;
  trackProtocolEvents: TrackProtocolEvents;
  record: ProtocolRecord;
  handleOpenTemplateModal: (protocolId: string, protocolName: string) => void;
  enableProtocolTemplates: boolean;
}

/**
 * Renders either the archived-row unarchive action or the standard row action
 * dropdown for the protocols table.
 *
 * @param props Render dependencies and row data.
 * @returns The action cell content for the current row.
 */
const renderProtocolActions = ({
  activeTab,
  checkTrailCount,
  getTranslatedText,
  handleArchive,
  handleDelete,
  handleUnarchive,
  trackProtocolEvents,
  record,
  handleOpenTemplateModal,
  enableProtocolTemplates,
}: RenderProtocolActionsProps): JSX.Element => {
  if (activeTab === "archived") {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <ProtocolUnarchiveActionButton
          recordId={record.id}
          getTranslatedText={getTranslatedText}
          handleUnarchive={handleUnarchive}
        />
      </div>
    );
  }

  const items = getProtocolActionMenuItems({
    checkTrailCount,
    getTranslatedText,
    handleArchive,
    handleDelete,
    trackProtocolEvents,
    record,
    activeTab,
    handleOpenTemplateModal,
    enableProtocolTemplates,
  });

  return (
    <div
      style={{ display: "flex", justifyContent: "center" }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Dropdown
        menu={{ items, className: "action-menu" }}
        trigger={["click"]}
        placement="bottomRight"
        overlayStyle={{
          minWidth: 160,
          zIndex: 1000,
        }}
      >
        <ProtocolActionsMenuButton />
      </Dropdown>
    </div>
  );
};

/**
 * Builds the column configuration for the protocols table, including
 * permission-aware status and action columns.
 *
 * @param handleDelete Callback used to start protocol deletion.
 * @param handleArchive Callback used to archive a protocol.
 * @param handleUnarchive Callback used to unarchive a protocol.
 * @param activeTab Currently selected table tab.
 * @returns The filtered column configuration for the protocols table.
 */
export const ProtocolsColumns = (
  handleDelete: any,
  handleArchive: (id: string) => Promise<void>,
  handleUnarchive: (id: string) => Promise<void>,
  activeTab: string,
  handleOpenTemplateModal: (protocolId: string, protocolName: string) => void
): any => {
  const { t } = useTranslation();
  const { enableProtocolTemplates } = useFeatureFlags();

  /**
   * Wraps translation lookup with the project's formatting helper.
   *
   * @param key Translation key.
   * @returns The formatted localized string.
   */
  const getTranslatedText = (key: string): string => formatTranslation(t(key));

  const trackProtocolEvents = useTrackingEvent({
    page: "Protocols Page",
    group: "Protocols",
    module: "Protocols",
  });
  /**
   * Normalizes the row trial count into a number.
   *
   * @param val Raw trial count value from the table data.
   * @returns Parsed numeric trial count.
   */
  const checkTrailCount = (val: string): number => {
    return parseInt(val, 10);
  };

  const { workspaceId, orgId } = useProtocolsRouter();
  const { data: permissionsResponse } = useSyncPermissions({
    workspaceId,
    orgId,
  });
  const allowed: PermissionOperation[] =
    permissionsResponse?.allowed_operations ?? [];

  const isWorkspaceOwner = isWorkspaceOwnerOperationAllowed(allowed);
  const isOrgAdmin = isOrgAdminOperationAllowed(allowed);
  const isOrgCollaboratorOnly = isOrgCollaboratorOnlyOperationAllowed(allowed);
  const showStatusColumn =
    (isWorkspaceOwner || isOrgAdmin) &&
    !isOrgCollaboratorOnly &&
    activeTab !== "templates";
  const showTrialsColumn = activeTab !== "templates";
  const showActionsColumn =
    (isWorkspaceOwner || isOrgAdmin) && !isOrgCollaboratorOnly;

  const PROTOCOLS_COLUMNS: any[] = [
    {
      title:
        activeTab === "templates"
          ? formatTranslation(t("createProtocol.basicInfo.templateName"))
          : formatTranslation(t("protocols.tableHeaderProtocolName")),
      dataIndex: "name",
      key: "name",
      sorter: (a: any, b: any) => a.name.localeCompare(b.name),
      render: (text: string) => <span>{text}</span>,
      width: "20%",
    },
    {
      title: formatTranslation(t("protocols.tableHeaderCreationDate")),
      dataIndex: "created_at",
      key: "createdAt",
      sorter: (a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      render: (creationDate: any) => formatDate(creationDate),
    },
    {
      title: formatTranslation(t("createProtocol.summary.type")),
      key: "protocol_type",
      dataIndex: ["protocol_type"],
      render: (types: string | string[] | null | undefined) =>
        renderProtocolTypeTags(types, getTranslatedText),

      // else return types.map((type: string) => type).join(" ");
    },
    {
      title: formatTranslation(
        t("createProtocol.objectives.objectiveItemTarget")
      ),
      dataIndex: "objectives",
      render: (objectives: any) => {
        if (objectives === null) return "";
        else {
          // Flatten all targets, remove duplicates, and join by comma
          const allTargets = [
            ...new Set(
              objectives.flatMap((objective: any) => objective.targets)
            ),
          ];
          return allTargets.join(", ");
        }
      },
      key: "target",
    },
    {
      title: formatTranslation(t("createProtocol.basicInfo.protocolCrop")),
      dataIndex: "crop",
      key: "crop",
      sorter: (a: any, b: any) => a.crop.localeCompare(b.crop),
    },
    {
      title: formatTranslation(t("trials")),
      dataIndex: "trial_count",
      key: "trials",
      sorter: (a: any, b: any) =>
        parseInt(a.trial_count) - parseInt(b.trial_count),
    },
    {
      title: formatTranslation(t("protocols.executionStatus")),
      dataIndex: "trial_execution_summary",
      key: "executionStatus",
      render: (executionSummary: ExecutionStatusCounts | undefined) =>
        renderExecutionStatus(executionSummary, getTranslatedText),
    },
    {
      title: formatTranslation(t("trials.status")),
      dataIndex: "status",
      key: "status",
      filters:
        activeTab === "active"
          ? [
              {
                text: formatTranslation(t("protocolFilter.draft")),
                value: "draft",
              },
              {
                text: formatTranslation(t("protocolFilter.published")),
                value: "published",
              },
            ]
          : null,
      onFilter:
        activeTab === "active"
          ? (value: string, record: any) =>
              record.status.toLowerCase() === value.toLowerCase()
          : null,
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
      render: (status: string, record: any) => (
        <span
          className={`status-column ${
            record.archived
              ? "protocol-archived"
              : status === "published"
              ? "protocol-published"
              : "protocol-draft"
          }`}
        >
          {record.archived
            ? formatTranslation(t("protocols.statusArchived"))
            : status?.toUpperCase()}
        </span>
      ),
    },
  ];
  // return PROTOCOLS_COLUMNS;
  const filteredColumns: any[] = PROTOCOLS_COLUMNS.filter((col) => {
    if (col.key === "status" && !showStatusColumn) return false;
    if (col.dataIndex === "actions" && !showActionsColumn) return false;
    if (
      (col.dataIndex === "trial_count" || col.key === "executionStatus") &&
      !showTrialsColumn
    )
      return false;
    return true;
  });

  if (activeTab === "templates") {
    filteredColumns.push({
      title: formatTranslation(t("createProtocol.basicInfo.protocolsusing")),
      dataIndex: "protocols_count",
      key: "protocols_count",
      render: (protocolsCount: number) => (
        <span>
          {protocolsCount}{" "}
          {protocolsCount <= 1
            ? formatTranslation(t("templates.noOfProtocol"))
            : formatTranslation(t("templates.noOfProtocols"))}
        </span>
      ),
      sorter: (a: any, b: any) => a.protocols_count - b.protocols_count,
    });
  }

  if (showActionsColumn) {
    filteredColumns.push({
      title: formatTranslation(t("trials.tableTitle7")),
      dataIndex: "actions",
      render: (text: any, record: ProtocolRecord) =>
        renderProtocolActions({
          activeTab,
          checkTrailCount,
          getTranslatedText,
          handleArchive,
          handleDelete,
          handleUnarchive,
          trackProtocolEvents,
          record,
          handleOpenTemplateModal,
          enableProtocolTemplates,
        }),
      fixed: true,
      width: "auto",
    });
  }

  return filteredColumns;
};

export {
  getProtocolActionMenuItems,
  getProtocolTypeDetails,
  hasTrialsAssigned,
  isOrgAdminOperationAllowed,
  isOrgCollaboratorOnlyOperationAllowed,
  isWorkspaceOwnerOperationAllowed,
  ProtocolActionsMenuButton,
  ProtocolArchiveActionButton,
  DeleteActionButton,
  ProtocolEditActionButton,
  ProtocolUnarchiveActionButton,
  SaveAsTemplateActionButton,
  UseTemplateActionButton,
  renderExecutionStatus,
  renderProtocolActions,
  renderProtocolTypeTags,
};
